import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  CheckCircle2,
  Send,
  User,
  Building2,
  Mail,
  Phone,
  MessageSquare,
  Truck,
  Fuel,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FUELING_NEEDS = [
  "Bid",
  "Bulk",
  "CFN",
  "DEF",
  "Fuel Mgmt System",
  "Generator",
  "Lubes",
  "Oil",
  "Other Services",
  "Propane",
  "Tank",
  "Wet Hosing",
];

const Field = ({ id, label, icon: Icon, children, required }) => (
  <div className="space-y-1.5">
    <label
      htmlFor={id}
      className="block text-xs font-bold uppercase tracking-wider text-[#404041]"
    >
      {label} {required && <span className="text-[#ecac1f]">*</span>}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-[#63499d] z-10">
        <Icon className="w-4 h-4" strokeWidth={2.25} />
      </div>
      {children}
    </div>
  </div>
);

const formatSnapshot = ({
  fleetSize,
  monthlyGallons,
  tripsPerWeek,
  laborSavings,
  minutesPerTrip,
  peopleInVehicle,
}) => {
  const fmtN = (n) =>
    new Intl.NumberFormat("en-US").format(Number(n) || 0);
  const fmtM = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  const lines = [
    "--- Fuel Savings Calculator Snapshot ---",
    `Vehicles in fleet: ${fmtN(fleetSize)}`,
    `Gallons purchased / month: ${fmtN(monthlyGallons)}`,
    `Fueling trips / vehicle / week: ${fmtN(tripsPerWeek)}`,
  ];
  if (minutesPerTrip !== undefined && minutesPerTrip !== null && minutesPerTrip !== "") {
    lines.push(`Time per fueling trip (min): ${fmtN(minutesPerTrip)}`);
  }
  if (peopleInVehicle !== undefined && peopleInVehicle !== null && peopleInVehicle !== "") {
    lines.push(`Employees in vehicle when fueling: ${fmtN(peopleInVehicle)}`);
  }
  lines.push(`Estimated annual labor savings: ${fmtM(laborSavings)}`);
  lines.push("---", "");
  return lines.join("\n");
};

export const LeadForm = ({
  fleetSize,
  monthlyGallons,
  tripsPerWeek,
  laborSavings,
  minutesPerTrip,
  peopleInVehicle,
}) => {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    fuelingNeeds: "-None-",
    otherInfo: "",
  });

  // Auto-prefill snapshot on open; refresh latest values each reopen
  useEffect(() => {
    if (!open) return;
    const snapshot = formatSnapshot({
      fleetSize,
      monthlyGallons,
      tripsPerWeek,
      laborSavings,
      minutesPerTrip,
      peopleInVehicle,
    });
    setForm((prev) => {
      const stripped = prev.otherInfo.replace(
        /--- Fuel Savings Calculator Snapshot ---[\s\S]*?---\n/,
        ""
      );
      return { ...prev, otherInfo: snapshot + stripped };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const validate = () => {
    if (!form.firstName.trim()) return "Please enter your first name.";
    if (!form.lastName.trim()) return "Please enter your last name.";
    if (!form.company.trim()) return "Please enter your company name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return "Please enter a valid email address.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/zoho-lead`, {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        fueling_needs: form.fuelingNeeds,
        other_info: form.otherInfo.trim(),
      });
      setSubmitted(true);
      toast.success("Thanks! A Ricochet specialist will be in touch shortly.");
    } catch (err2) {
      console.error(err2);
      const msg =
        err2?.response?.data?.detail ||
        "Something went wrong. Please call 833-925-5065.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ===== SUCCESS STATE =====
  if (submitted) {
    return (
      <div
        className="mt-6 bg-white border-2 border-[#63499d]/20 rounded-2xl p-6 sm:p-8 text-center"
        data-testid="lead-form-success"
      >
        <div className="w-14 h-14 rounded-full bg-[#ecac1f]/20 text-[#63499d] grid place-items-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8" strokeWidth={2.25} />
        </div>
        <h3 className="font-display font-bold text-2xl text-[#404041] mb-2">
          Got it — we'll be in touch!
        </h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          A Ricochet fuel specialist will reach out within one business day with
          a detailed savings analysis tailored to your fleet.
        </p>
      </div>
    );
  }

  // ===== COLLAPSED PROMPT =====
  if (!open) {
    return (
      <div
        className="mt-6 bg-white border-2 border-dashed border-[#63499d]/30 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print"
        data-testid="lead-form-prompt"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-2 h-2 rounded-full bg-[#ecac1f] animate-pulse" />
            <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#63499d]">
              Talk to a Specialist
            </span>
          </div>
          <div className="font-display font-bold text-lg sm:text-xl text-[#404041] leading-tight">
            Request a detailed savings proposal
          </div>
          <div className="text-sm text-slate-500 mt-1">
            We'll review your fleet numbers and send a custom quote — free, no
            obligation.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-[#2a1f4a] bg-[#ecac1f] hover:bg-[#d99812] transition-colors shrink-0"
          data-testid="lead-form-open-button"
        >
          Get My Quote <Send className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // ===== EXPANDED FORM =====
  return (
    <form
      noValidate
      onSubmit={handleSubmit}
      className="mt-6 bg-white border-2 border-[#63499d]/20 rounded-2xl p-6 sm:p-8 no-print animate-fade-up"
      data-testid="lead-form"
    >
      <div className="flex items-center gap-3 mb-1">
        <span className="gold-bar" />
        <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#63499d]">
          Step 3 — Fuel Request
        </span>
      </div>
      <h3 className="font-display font-bold text-xl sm:text-2xl text-[#404041] mb-1">
        Tell us about your fleet
      </h3>
      <p className="text-sm text-slate-500 mb-6">
        A Ricochet specialist will follow up within one business day. Your
        calculator results are auto-included below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <Field id="lead-first-name" label="First Name" icon={User} required>
          <input
            id="lead-first-name"
            type="text"
            maxLength={40}
            value={form.firstName}
            onChange={update("firstName")}
            placeholder="John"
            autoComplete="given-name"
            className="brand-input w-full pl-10 pr-3 py-3 text-sm font-medium text-[#404041] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors"
            data-testid="lead-input-first-name"
          />
        </Field>

        <Field id="lead-last-name" label="Last Name" icon={User} required>
          <input
            id="lead-last-name"
            type="text"
            maxLength={80}
            value={form.lastName}
            onChange={update("lastName")}
            placeholder="Smith"
            autoComplete="family-name"
            className="brand-input w-full pl-10 pr-3 py-3 text-sm font-medium text-[#404041] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors"
            data-testid="lead-input-last-name"
          />
        </Field>

        <Field id="lead-company" label="Company Name" icon={Building2} required>
          <input
            id="lead-company"
            type="text"
            maxLength={200}
            value={form.company}
            onChange={update("company")}
            placeholder="Acme Logistics"
            autoComplete="organization"
            className="brand-input w-full pl-10 pr-3 py-3 text-sm font-medium text-[#404041] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors"
            data-testid="lead-input-company"
          />
        </Field>

        <Field id="lead-email" label="Email" icon={Mail} required>
          <input
            id="lead-email"
            type="text"
            inputMode="email"
            maxLength={100}
            value={form.email}
            onChange={update("email")}
            placeholder="john@acme.com"
            autoComplete="email"
            className="brand-input w-full pl-10 pr-3 py-3 text-sm font-medium text-[#404041] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors"
            data-testid="lead-input-email"
          />
        </Field>

        <Field id="lead-phone" label="Phone" icon={Phone}>
          <input
            id="lead-phone"
            type="tel"
            maxLength={50}
            value={form.phone}
            onChange={update("phone")}
            placeholder="(555) 555-5555"
            autoComplete="tel"
            className="brand-input w-full pl-10 pr-3 py-3 text-sm font-medium text-[#404041] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors"
            data-testid="lead-input-phone"
          />
        </Field>

        <Field id="lead-fueling-needs" label="Fueling Needs" icon={Fuel}>
          <select
            id="lead-fueling-needs"
            value={form.fuelingNeeds}
            onChange={update("fuelingNeeds")}
            className="brand-input w-full pl-10 pr-9 py-3 text-sm font-medium text-[#404041] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors appearance-none"
            data-testid="lead-input-fueling-needs"
          >
            <option value="-None-">Select an option</option>
            {FUELING_NEEDS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3.5 pointer-events-none text-slate-400">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </Field>

        <div className="sm:col-span-2">
          <Field
            id="lead-other-info"
            label="Other Information"
            icon={MessageSquare}
          >
            <textarea
              id="lead-other-info"
              value={form.otherInfo}
              onChange={update("otherInfo")}
              rows={7}
              maxLength={32000}
              placeholder="Anything we should know about your operation?"
              className="brand-input w-full pl-10 pr-3 py-3 text-sm text-[#404041] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors resize-y font-mono"
              data-testid="lead-input-other-info"
            />
          </Field>
        </div>
      </div>

      <div className="mt-5 p-4 bg-[#f7f5fb] border border-[#63499d]/10 rounded-xl text-xs text-slate-500 leading-relaxed flex items-start gap-2">
        <Truck className="w-4 h-4 text-[#63499d] shrink-0 mt-0.5" />
        <div>
          Your calculator inputs (
          <span className="font-semibold text-[#404041]">
            {Number(fleetSize) || 0} vehicles
          </span>
          {" • "}
          <span className="font-semibold text-[#404041]">
            {Number(monthlyGallons || 0).toLocaleString()} gal/mo
          </span>
          {" • "}
          <span className="font-semibold text-[#404041]">
            {Number(tripsPerWeek) || 0} trips/wk
          </span>
          ) are auto-included in the message above for our specialist. Feel
          free to add anything else.
        </div>
      </div>

      <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-sm font-semibold text-slate-500 hover:text-[#404041] transition-colors"
          data-testid="lead-form-cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-[#2a1f4a] bg-[#ecac1f] hover:bg-[#d99812] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="lead-form-submit"
        >
          {submitting ? "Sending..." : "Submit Request"}
          <Send className="w-4 h-4" />
        </button>
      </div>

      <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
        By submitting, you agree to be contacted by Ricochet Fuel Distributors
        about fuel delivery services. We never share your info.
      </p>
    </form>
  );
};
